import React, { useCallback, useRef, useState } from 'react';
import type { OfficialAssetServerMaterial } from '$/api/server.types';
import type { PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import { useLanguageStore } from '$/store/languageStore';
import { openPreview } from '$/store/usePreviewStore';
import type { RoleGenFormValues, RoleGenMaterialCategory } from '../roleFormTypes';
import {
  buildMaterialValuePatch,
  getRoleGenOfficialMaterialImages,
  loadRoleGenOfficialMaterialPatch,
} from '../roleMaterialUtils';
import RoleReferenceUploader from '../RoleReferenceUploader';
import RoleMaterialRow, { type RoleMaterialRowConfig } from '../RoleMaterialRow';
import {
  roleGenMaterialCategoryToUploadLocation,
  type RoleAssetUploadLocation,
  type RoleAssetUploadTriggerType,
} from '../../../tracking';
import styles from './index.module.scss';

export {
  INITIAL_ROLE_GEN_FORM_VALUES,
  type RoleGenAgeStage,
  type RoleGenFormValues,
  type RoleGenGender,
  type RoleGenMaterialCategory,
  type RoleGenMaterialFilter,
  type RoleGenMaterialSource,
  type RoleGenModelParameterValue,
} from '../roleFormTypes';

interface RoleSettingFormProps {
  values: RoleGenFormValues;
  onChange: (patch: Partial<RoleGenFormValues>) => void;
  onOpenMaterialSelector: (category: RoleGenMaterialCategory) => void;
  onUploadReferenceImage: (file: File) => void;
  onHistoryUploadReference: () => void;
  onLocalUploadMaterial: (category: RoleGenMaterialCategory, file: File) => void;
  onHistoryUploadMaterial: (category: RoleGenMaterialCategory) => void;
  onUploadControlClick: (location: RoleAssetUploadLocation, triggerType: RoleAssetUploadTriggerType) => void;
  uploadingReferenceImage?: boolean;
  uploadingMaterialCategory?: RoleGenMaterialCategory | null;
}

const NAME_MAX_LENGTH = 20;
const PROMPT_MAX_LENGTH = 500;
const MATERIAL_UPLOAD_POPOVER_Z_INDEX = 5800;

/** 将当前素材的官方多图或上传单图转换为公共放大预览列表。 */
function buildRoleMaterialPreviewItems(
  values: RoleGenFormValues,
  category: RoleGenMaterialCategory,
): PreviewMediaItem[] {
  const materialValues: Record<RoleGenMaterialCategory, {
    officialMaterial?: OfficialAssetServerMaterial;
    coverUrl?: string;
    name?: string;
  }> = {
    clothing: {
      officialMaterial: values.clothingOfficialMaterial,
      coverUrl: values.clothingMaterialCoverUrl,
      name: values.clothingMaterialName,
    },
    hairstyle: {
      officialMaterial: values.hairstyleOfficialMaterial,
      coverUrl: values.hairstyleMaterialCoverUrl,
      name: values.hairstyleMaterialName,
    },
    accessory: {
      officialMaterial: values.accessoryOfficialMaterial,
      coverUrl: values.accessoryMaterialCoverUrl,
      name: values.accessoryMaterialName,
    },
  };
  const currentMaterial = materialValues[category];
  let sourceImages: Array<{ url: string; width?: number; height?: number; }> = [];
  if (currentMaterial.officialMaterial) {
    sourceImages = getRoleGenOfficialMaterialImages(currentMaterial.officialMaterial);
  } else if (currentMaterial.coverUrl) {
    sourceImages = [{ url: currentMaterial.coverUrl }];
  }

  return sourceImages.map((image, index) => ({
    type: 'image',
    task_id: `role-material-${category}-${currentMaterial.officialMaterial?.id ?? 'custom'}-${index}`,
    url: image.url,
    width: image.width,
    height: image.height,
    name: currentMaterial.name,
  }));
}

/**
 * 角色设定表单（步骤 1）；负责聚合基础信息、参考图和参考素材输入。
 *
 * @param props 见 RoleSettingFormProps
 * @returns 表单节点
 */
function RoleSettingForm({
  values,
  onChange,
  onOpenMaterialSelector,
  onUploadReferenceImage,
  onHistoryUploadReference,
  onLocalUploadMaterial,
  onHistoryUploadMaterial,
  onUploadControlClick,
  uploadingReferenceImage,
  uploadingMaterialCategory,
}: RoleSettingFormProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const referenceFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadCategoryRef = useRef<RoleGenMaterialCategory | null>(null);
  const [uploadPopoverTarget, setUploadPopoverTarget] = useState<RoleGenMaterialCategory | 'reference' | null>(null);
  const [referenceDragActive, setReferenceDragActive] = useState(false);
  const materialRows: RoleMaterialRowConfig[] = [
    {
      key: 'clothingMaterialId',
      nameKey: 'clothingMaterialName',
      coverKey: 'clothingMaterialCoverUrl',
      category: 'clothing',
      label: languageStore.smartcatTranslationText((value) => value.clothing),
      placeholder: languageStore.smartcatTranslationText((value) => value.please_select_clothing),
    },
    {
      key: 'hairstyleMaterialId',
      nameKey: 'hairstyleMaterialName',
      coverKey: 'hairstyleMaterialCoverUrl',
      category: 'hairstyle',
      label: languageStore.smartcatTranslationText((value) => value.hairstyle),
      placeholder: languageStore.smartcatTranslationText((value) => value.please_select_a_hairstyle),
    },
    {
      key: 'accessoryMaterialId',
      nameKey: 'accessoryMaterialName',
      coverKey: 'accessoryMaterialCoverUrl',
      category: 'accessory',
      label: languageStore.smartcatTranslationText((value) => value.accessories),
      placeholder: languageStore.smartcatTranslationText((value) => value.please_select_accessories),
    },
  ];

  /** 更新表单单字段值，保持父组件统一聚合表单状态。 */
  const patch = useCallback(<K extends keyof RoleGenFormValues>(key: K, value: RoleGenFormValues[K]) => {
    onChange({ [key]: value } as Partial<RoleGenFormValues>);
  }, [onChange]);

  /** 点击参考图本地上传选项时触发隐藏 input，进入原图片上传链路。 */
  const handleReferenceUploadClick = useCallback((): void => {
    if (uploadingReferenceImage) return;
    onUploadControlClick('role_reference', 'click');
    setUploadPopoverTarget(null);
    referenceFileInputRef.current?.click();
  }, [onUploadControlClick, uploadingReferenceImage]);

  /** 打开参考图历史记录前关闭上传来源菜单。 */
  const handleReferenceHistoryUploadClick = useCallback((): void => {
    setUploadPopoverTarget(null);
    onHistoryUploadReference();
  }, [onHistoryUploadReference]);

  /** 选择本地角色参考图后交给父组件上传，并清空 input 以支持重复选择同一文件。 */
  const handleReferenceFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    onUploadReferenceImage(file);
  }, [onUploadReferenceImage]);

  /** 拖入角色参考图上传区时高亮边框，提示当前区域可投放图片。 */
  const handleReferenceDragEnter = useCallback((event: React.DragEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    if (uploadingReferenceImage) return;
    setReferenceDragActive(true);
  }, [uploadingReferenceImage]);

  /** 拖拽悬停时阻止浏览器默认打开文件行为，保持投放上传交互。 */
  const handleReferenceDragOver = useCallback((event: React.DragEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    if (uploadingReferenceImage) return;
    setReferenceDragActive(true);
  }, [uploadingReferenceImage]);

  /** 离开角色参考图上传区时取消高亮状态。 */
  const handleReferenceDragLeave = useCallback((event: React.DragEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setUploadPopoverTarget(null);
    setReferenceDragActive(false);
  }, []);

  /** 投放文件后取第一张图片作为角色参考图并复用父组件上传逻辑。 */
  const handleReferenceDrop = useCallback((event: React.DragEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setReferenceDragActive(false);
    if (uploadingReferenceImage) return;
    const file = Array.from(event.dataTransfer.files).find((item) => item.type.startsWith('image/'));
    if (!file) return;
    onUploadControlClick('role_reference', 'drop');
    onUploadReferenceImage(file);
  }, [onUploadControlClick, onUploadReferenceImage, uploadingReferenceImage]);

  /** 点击本地上传选项时记录当前素材类型，再触发隐藏文件选择框。 */
  const handleLocalUploadClick = useCallback((category: RoleGenMaterialCategory): void => {
    onUploadControlClick(roleGenMaterialCategoryToUploadLocation(category), 'click');
    setUploadPopoverTarget(null);
    uploadCategoryRef.current = category;
    fileInputRef.current?.click();
  }, [onUploadControlClick]);

  /** 打开历史记录上传前先收起当前上传菜单，避免二级弹框被菜单遮挡。 */
  const handleHistoryUploadClick = useCallback((category: RoleGenMaterialCategory): void => {
    setUploadPopoverTarget(null);
    onHistoryUploadMaterial(category);
  }, [onHistoryUploadMaterial]);

  /** 受控管理每个素材行的上传菜单开关，同一时间只展开一个菜单。 */
  const handleUploadPopoverOpenChange = useCallback((
    category: RoleGenMaterialCategory,
    nextOpen: boolean,
  ): void => {
    setUploadPopoverTarget(nextOpen ? category : null);
  }, []);

  /** 点击已选素材缩略图时按需补全官方详情，再打开无业务操作按钮的公共预览。 */
  const handlePreviewMaterial = useCallback(async (category: RoleGenMaterialCategory): Promise<void> => {
    const materialPatch = await loadRoleGenOfficialMaterialPatch(values, [category]);
    const previewValues = Object.keys(materialPatch).length > 0
      ? { ...values, ...materialPatch }
      : values;
    const previewImageList = buildRoleMaterialPreviewItems(previewValues, category);
    if (previewImageList.length === 0) return;
    setUploadPopoverTarget(null);
    openPreview({
      previewImageList,
      initialIndex: 0,
      previewShowDownload: false,
      previewShowQuote: false,
      previewShowZoom: false,
      previewHeaderMenuSlots: null,
    });
  }, [values]);

  /** 删除当前素材时同步清空展示、来源、官方素材对象和筛选条件。 */
  const handleClearMaterial = useCallback((category: RoleGenMaterialCategory): void => {
    setUploadPopoverTarget(null);
    onChange(buildMaterialValuePatch(category, {
      id: '',
      name: '',
      coverUrl: '',
    }));
  }, [onChange]);

  /** 文件选择完成后把文件交给父组件上传，并清空 input 以支持连续选择同一文件。 */
  const handleLocalFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const category = uploadCategoryRef.current;
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (!category || !file) return;
    onLocalUploadMaterial(category, file);
  }, [onLocalUploadMaterial]);

  return (
    <div className={styles.formRoot}>
      <input
        ref={referenceFileInputRef}
        className={styles.materialUploadInput}
        type="file"
        accept="image/*"
        onChange={handleReferenceFileChange}
      />
      <input
        ref={fileInputRef}
        className={styles.materialUploadInput}
        type="file"
        accept="image/*"
        onChange={handleLocalFileChange}
      />
      <section className={styles.roleSection}>
        <h3 className={styles.sectionTitle}>
          {languageStore.smartcatTranslationText((value) => value.character_setting)}
        </h3>
        <div className={styles.roleSettingGrid}>
          <div className={styles.roleTextFields}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                {languageStore.smartcatTranslationText((value) => value.robo_juese_name)}
                <span className={styles.requiredMark}>*</span>
              </span>
              <input
                className={styles.textInput}
                type="text"
                value={values.name}
                maxLength={NAME_MAX_LENGTH}
                onChange={(e) => patch('name', e.target.value)}
                placeholder={languageStore.smartcatTranslationText((value) => value.enter_character_s_name)}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                {languageStore.smartcatTranslationText((value) => value.robo_juese_desc)}
                <span className={styles.requiredMark}>*</span>
              </span>
              <span className={styles.textareaWrap}>
                <textarea
                  className={styles.textarea}
                  value={values.prompt}
                  maxLength={PROMPT_MAX_LENGTH}
                  onChange={(e) => patch('prompt', e.target.value)}
                  placeholder={languageStore.smartcatTranslationText(
                    (value) => value.describe_character_traits_generate_requirements_,
                  )}
                />
                <span className={styles.textareaCount}>
                  {`${values.prompt.length}/${PROMPT_MAX_LENGTH}`}
                </span>
              </span>
            </div>
          </div>
          <RoleReferenceUploader
            imageUrl={values.referenceImageUrl}
            uploading={uploadingReferenceImage}
            dragActive={referenceDragActive}
            uploadPopoverOpen={uploadPopoverTarget === 'reference'}
            popoverZIndex={MATERIAL_UPLOAD_POPOVER_Z_INDEX}
            onUploadPopoverOpenChange={(nextOpen) => setUploadPopoverTarget(nextOpen ? 'reference' : null)}
            onLocalUpload={handleReferenceUploadClick}
            onHistoryUpload={handleReferenceHistoryUploadClick}
            onDragEnter={handleReferenceDragEnter}
            onDragOver={handleReferenceDragOver}
            onDragLeave={handleReferenceDragLeave}
            onDrop={handleReferenceDrop}
          />
        </div>
      </section>

      <section className={styles.materialSection}>
        <h3 className={styles.sectionTitle}>
          {languageStore.smartcatTranslationText((value) => value.choose_reference_material)}
        </h3>
        <div className={styles.materialList}>
          {materialRows.map((row) => (
            <RoleMaterialRow
              key={row.key}
              row={row}
              values={values}
              uploading={uploadingMaterialCategory === row.category}
              uploadPopoverOpen={uploadPopoverTarget === row.category}
              popoverZIndex={MATERIAL_UPLOAD_POPOVER_Z_INDEX}
              onSelectMaterial={onOpenMaterialSelector}
              onPreviewMaterial={handlePreviewMaterial}
              onClearMaterial={handleClearMaterial}
              onLocalUpload={handleLocalUploadClick}
              onHistoryUpload={handleHistoryUploadClick}
              onUploadPopoverOpenChange={handleUploadPopoverOpenChange}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

export default RoleSettingForm;
